import React, { useState, useEffect } from 'react';

const GitHubActionsSidebar = () => {
  const [workflows, setWorkflows] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(true); // Start closed
  const [hoveredFile, setHoveredFile] = useState(null);
  const [fileContent, setFileContent] = useState({});
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Mock workflow data - in production, this would fetch from GitHub API
  useEffect(() => {
    const mockWorkflows = [
      {
        id: 1,
        name: 'Content Discovery',
        description: 'Scans for missing syllabi and vocabulary files',
        status: 'success',
        lastRun: '2 hours ago',
        nextRun: 'Daily at 2 AM UTC',
        icon: '🔍',
        file: 'discover-content-gaps.yaml'
      },
      {
        id: 2,
        name: 'Syllabus Generation',
        description: 'Creates comprehensive course syllabi using AI',
        status: 'running',
        lastRun: 'Running now',
        nextRun: 'On demand / Daily check',
        icon: '📚',
        file: 'generate-syllabus.yaml'
      },
      {
        id: 3,
        name: 'Vocabulary Generation',
        description: 'Generates weekly vocabulary lists for courses',
        status: 'success',
        lastRun: '1 day ago',
        nextRun: 'Weekly on Monday',
        icon: '📝',
        file: 'generate-vocabulary.yaml'
      },
      {
        id: 4,
        name: 'Content Validation',
        description: 'AI-powered quality assurance for educational content',
        status: 'success',
        lastRun: '4 hours ago',
        nextRun: 'Daily at 4 AM UTC',
        icon: '✅',
        file: 'validate-content.yaml'
      },
      {
        id: 5,
        name: 'Course Updates',
        description: 'Updates core.js with new courses and metadata',
        status: 'pending',
        lastRun: '3 days ago',
        nextRun: 'On content changes',
        icon: '🔄',
        file: 'update-course-list.yaml'
      },
      {
        id: 6,
        name: 'Production Monitor',
        description: 'Monitors platform performance and engagement',
        status: 'success',
        lastRun: '6 hours ago',
        nextRun: 'Every 6 hours',
        icon: '📊',
        file: 'production-monitoring.yaml'
      }
    ];

    setWorkflows(mockWorkflows);

    // Mock file content for GitHub Actions
    const mockFileContent = {
      'discover-content-gaps.yaml': `name: 📋 Discover Content Gaps

on:
  schedule:
    - cron: '0 2 * * *'
  workflow_dispatch:

jobs:
  discover-gaps:
    runs-on: ubuntu-latest
    steps:
    - name: Checkout repository
      uses: actions/checkout@v4
    - name: Scan for missing syllabi
      run: |
        echo "Scanning for courses without syllabi..."
        # Check for missing course content`,
      'generate-syllabus.yaml': `name: 📚 Generate Syllabus

on:
  workflow_dispatch:
    inputs:
      course_id:
        description: 'Course ID to generate syllabus for'
        required: true
        type: string

jobs:
  generate-syllabus:
    runs-on: ubuntu-latest
    steps:
    - name: Checkout repository
      uses: actions/checkout@v4
    - name: Generate syllabus with AI
      env:
        OPENAI_API_KEY: \${{ secrets.OPENAI_API_KEY }}
      run: |
        echo "Generating syllabus for \${{ inputs.course_id }}"`,
      'generate-vocabulary.yaml': `name: 📝 Generate Vocabulary

on:
  schedule:
    - cron: '0 3 * * 1'
  workflow_dispatch:

jobs:
  generate-vocabulary:
    runs-on: ubuntu-latest
    steps:
    - name: Generate vocabulary files
      env:
        OPENAI_API_KEY: \${{ secrets.OPENAI_API_KEY }}
      run: |
        echo "Generating vocabulary lists..."`,
      'validate-content.yaml': `name: ✅ Content Validation

on:
  schedule:
    - cron: '0 4 * * *'
  push:
    paths:
      - 'public/syllabi/**'
      - 'public/coursework/**'

jobs:
  validate-content:
    runs-on: ubuntu-latest
    steps:
    - name: Validate educational content
      run: |
        echo "Validating content quality..."`,
      'update-course-list.yaml': `name: 🔄 Update Course List

on:
  push:
    paths:
      - 'public/syllabi/**'
  workflow_dispatch:

jobs:
  update-courses:
    runs-on: ubuntu-latest
    steps:
    - name: Update core.js with new courses
      run: |
        echo "Updating course metadata..."`,
      'production-monitoring.yaml': `name: 📊 Production Monitor

on:
  schedule:
    - cron: '0 */6 * * *'

jobs:
  monitor:
    runs-on: ubuntu-latest
    steps:
    - name: Monitor platform performance
      run: |
        echo "Checking platform metrics..."`
    };

    setFileContent(mockFileContent);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'text-green-600 bg-green-50 border-green-200';
      case 'running': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'failed': return 'text-red-600 bg-red-50 border-red-200';
      case 'pending': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return '✅';
      case 'running': return '🔄';
      case 'failed': return '❌';
      case 'pending': return '⏳';
      default: return '⚪';
    }
  };

  const handleFileHover = (fileName, event) => {
    setHoveredFile(fileName);
    setShowTooltip(true);
    setTooltipPosition({
      x: event.clientX,
      y: event.clientY
    });
  };

  const handleFileLeave = () => {
    setHoveredFile(null);
    setShowTooltip(false);
  };

  const copyFileContent = async (fileName) => {
    const content = fileContent[fileName];
    if (content) {
      try {
        await navigator.clipboard.writeText(content);
        // Show success feedback
        console.log(`Copied ${fileName} to clipboard`);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <div className={`fixed right-0 top-0 h-full bg-white border-l border-gray-200 shadow-lg transition-transform duration-300 z-50 w-full sm:w-80 md:w-96 lg:w-[400px] ${
      isCollapsed ? 'transform translate-x-full' : ''
    }`}>
      

      {!isCollapsed && (
        <div className="p-4 h-full overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between gap-2 mb-6 pb-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <div className="text-2xl">⚡</div>
              <div>
                <h2 className="font-semibold text-gray-900">GitHub Actions</h2>
                <p className="text-xs text-gray-500">Automation Status</p>
              </div>
            </div>
            <button
              onClick={() => setIsCollapsed(true)}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title="Hide GitHub Actions"
            >
              ✕
            </button>
          </div>

          {/* Workflows */}
          <div className="space-y-3">
            {workflows.map((workflow) => (
              <div
                key={workflow.id}
                className={`border rounded-lg p-3 transition-colors hover:bg-gray-50 ${getStatusColor(workflow.status)}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{workflow.icon}</span>
                    <div>
                      <h3 className="font-medium text-sm text-gray-900">{workflow.name}</h3>
                      <p className="text-xs text-gray-600 mt-1">{workflow.description}</p>
                    </div>
                  </div>
                  <span className="text-lg">{getStatusIcon(workflow.status)}</span>
                </div>

                <div className="text-xs text-gray-500 space-y-1">
                  <div className="flex justify-between">
                    <span>Last run:</span>
                    <span className="font-medium">{workflow.lastRun}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Next run:</span>
                    <span className="font-medium">{workflow.nextRun}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>File:</span>
                    <span 
                      className="font-mono text-xs bg-gray-100 px-2 py-1 rounded hover:bg-blue-100 hover:text-blue-700 cursor-pointer transition-colors"
                      onMouseEnter={(e) => handleFileHover(workflow.file, e)}
                      onMouseLeave={handleFileLeave}
                      onClick={() => copyFileContent(workflow.file)}
                      title="Click to copy file content"
                    >
                      📋 {workflow.file}
                    </span>
                  </div>
                </div>

              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="bg-gray-50 rounded-lg p-3">
              <h4 className="font-medium text-sm text-gray-900 mb-2">System Status</h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex justify-between">
                  <span>✅ Success:</span>
                  <span className="font-medium">{workflows.filter(w => w.status === 'success').length}</span>
                </div>
                <div className="flex justify-between">
                  <span>🔄 Running:</span>
                  <span className="font-medium">{workflows.filter(w => w.status === 'running').length}</span>
                </div>
                <div className="flex justify-between">
                  <span>⏳ Pending:</span>
                  <span className="font-medium">{workflows.filter(w => w.status === 'pending').length}</span>
                </div>
                <div className="flex justify-between">
                  <span>❌ Failed:</span>
                  <span className="font-medium">{workflows.filter(w => w.status === 'failed').length}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-4">
            <h4 className="font-medium text-sm text-gray-900 mb-2">Quick Actions</h4>
            <div className="space-y-1">
              <button className="w-full text-left text-xs bg-blue-50 text-blue-700 px-2 py-1.5 rounded hover:bg-blue-100 transition-colors">
                🔍 Scan for missing content
              </button>
              <button className="w-full text-left text-xs bg-green-50 text-green-700 px-2 py-1.5 rounded hover:bg-green-100 transition-colors">
                📚 Generate missing syllabi
              </button>
              <button className="w-full text-left text-xs bg-purple-50 text-purple-700 px-2 py-1.5 rounded hover:bg-purple-100 transition-colors">
                📝 Create vocabulary lists
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Content Tooltip */}
      {showTooltip && hoveredFile && fileContent[hoveredFile] && (
        <div 
          className="fixed z-[60] max-w-lg bg-gray-900 text-white text-xs rounded-lg p-4 shadow-xl pointer-events-none"
          style={{ 
            left: `${Math.min(tooltipPosition.x + 10, window.innerWidth - 300)}px`, 
            top: `${Math.max(tooltipPosition.y - 10, 10)}px`,
            maxHeight: '400px',
            overflow: 'auto'
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold text-blue-300">
              📄 {hoveredFile}
            </div>
            <div className="text-gray-400 text-xs">
              Click to copy
            </div>
          </div>
          <pre className="text-gray-200 text-xs leading-relaxed overflow-x-auto">
            {fileContent[hoveredFile]}
          </pre>
          <div className="text-gray-400 text-xs mt-2 pt-2 border-t border-gray-700">
            💡 This workflow automates educational content management
          </div>
        </div>
      )}
    </div>
  );
};

export default GitHubActionsSidebar;